import React, { useEffect, useState, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationsProvider, useNotifications } from "@/contexts/NotificationsContext";
import { UserCountsProvider } from "@/contexts/UserCountsContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import UserTabBar from "@/components/layout/UserTabBar";
import FloatingMenu from "@/components/layout/FloatingMenu";
import AdminFloatingMenu from "@/components/layout/AdminFloatingMenu";
import CookieBanner from "@/components/CookieBanner";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import ScrollRestoration from "@/components/ScrollRestoration";
import { settingsAPI, messagesAPI } from "@/lib/api";
import { useAutoPushSubscription } from "@/hooks/useAutoPushSubscription";
import { useFaviconBadge } from "@/hooks/useFaviconBadge";

// Pages
import HomePage from "@/pages/HomePage";
import { LoginPage, RegisterPage, AuthCallback, RegisterVerifyPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/AuthPages";
import CategoryPage from "@/pages/CategoryPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import CreateListingPage from "@/pages/CreateListingPage";
import DashboardPage from "@/pages/DashboardPage";
import AdminPage from "@/pages/AdminPage";
import MessagesPage from "@/pages/MessagesPage";
import NotificationsPage from "@/pages/NotificationsPage";
import OrderPage from "@/pages/OrderPage";
import UserProfilePage from "@/pages/UserProfilePage";
import NotificationSettingsPage from "@/pages/NotificationSettingsPage";
import AllSalesPage from "@/pages/AllSalesPage";
import AllAuctionsPage from "@/pages/AllAuctionsPage";
import AllListingsPage from "@/pages/AllListingsPage";
import TradesPage from "@/pages/TradesPage";
import MyDomainsPage from "@/pages/MyDomainsPage";
import StaticPage from "@/pages/StaticPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ContactPage from "@/pages/ContactPage";

// Protected Route component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
};

// App Router - handles OAuth callback detection
const AppRouter = () => {
    const location = useLocation();

    // Check URL fragment for session_id SYNCHRONOUSLY (before render)
    // This prevents race conditions with ProtectedRoute
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <>
            <ScrollRestoration />
            <Routes>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/register/verify" element={<RegisterVerifyPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/category/:slug/:subcategory" element={<CategoryPage />} />
            <Route path="/listing/:id" element={<ListingDetailPage />} />
            <Route path="/search" element={<CategoryPage />} />
            <Route path="/all-listings" element={<AllListingsPage />} />
            <Route path="/all-sales" element={<AllSalesPage />} />
            <Route path="/all-auctions" element={<AllAuctionsPage />} />
            <Route path="/trades" element={<TradesPage />} />
            <Route path="/my-domains" element={<ProtectedRoute><MyDomainsPage /></ProtectedRoute>} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* Protected routes */}
            <Route
                path="/dashboard"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/create-listing"
                element={
                    <ProtectedRoute>
                        <CreateListingPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/my-auctions"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/participating"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/watchlist"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings"
                element={
                    <ProtectedRoute>
                        <DashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/notifications/settings"
                element={
                    <ProtectedRoute>
                        <NotificationSettingsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin"
                element={
                    <ProtectedRoute>
                        <AdminPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages"
                element={
                    <ProtectedRoute>
                        <MessagesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/notifications"
                element={
                    <ProtectedRoute>
                        <NotificationsPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/messages/:conversationId"
                element={
                    <ProtectedRoute>
                        <MessagesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/orders/:orderId"
                element={
                    <ProtectedRoute>
                        <OrderPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/order/:orderId"
                element={
                    <ProtectedRoute>
                        <OrderPage />
                    </ProtectedRoute>
                }
            />

            {/* User Profile - requires authentication */}
            <Route path="/user/:username" element={
                <ProtectedRoute>
                    <UserProfilePage />
                </ProtectedRoute>
            } />
            
            {/* Static Pages */}
            <Route path="/despre-noi" element={<StaticPage slug="despre-noi" />} />
            <Route path="/cum-functioneaza" element={<StaticPage slug="cum-functioneaza" />} />
            <Route path="/termeni" element={<StaticPage slug="termeni" />} />
            <Route path="/confidentialitate" element={<StaticPage slug="confidentialitate" />} />
            <Route path="/ajutor" element={<StaticPage slug="ajutor" />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </>
    );
};

// Main App Layout
const AppLayout = () => {
    const location = useLocation();
    const { user, isAuthenticated } = useAuth();
    const { unreadCount: unreadNotifications } = useNotifications();
    const [unreadMessages, setUnreadMessages] = useState(0);
    const isAuthPage = ['/login', '/register'].includes(location.pathname) || location.hash?.includes('session_id=');
    const isAdminPage = location.pathname.startsWith('/admin');
    const isAdmin = user?.role === 'admin';
    
    // Auto-subscribe to push notifications when authenticated
    useAutoPushSubscription();
    
    // Heartbeat for active visitor tracking
    useEffect(() => {
        const sendHeartbeat = async () => {
            try {
                const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                const token = localStorage.getItem('token');
                const headers = {
                    'Content-Type': 'application/json'
                };
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                
                await fetch(`${API_URL}/api/heartbeat`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        current_page: location.pathname,
                        referrer: document.referrer || null
                    })
                });
            } catch (error) {
                // Silently ignore heartbeat errors
            }
        };
        
        // Send immediately on page change
        sendHeartbeat();
        
        // Then every 45 seconds
        const interval = setInterval(sendHeartbeat, 45000);
        
        return () => clearInterval(interval);
    }, [location.pathname]);
    
    // Fetch unread messages count
    const fetchUnreadMessages = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const res = await messagesAPI.getConversations();
            const conversations = res.data || [];
            const total = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
            setUnreadMessages(total);
        } catch (error) {
            // Ignore errors
        }
    }, [isAuthenticated]);
    
    // Fetch unread messages on mount and periodically
    useEffect(() => {
        fetchUnreadMessages();
        const interval = setInterval(fetchUnreadMessages, 5000); // Every 5 seconds for faster favicon update
        return () => clearInterval(interval);
    }, [fetchUnreadMessages]);
    
    // Favicon badge for unread messages/notifications
    const totalUnread = (unreadNotifications || 0) + (unreadMessages || 0);
    useFaviconBadge(totalUnread);
    
    // Show footer only on homepage for non-authenticated users
    const showFooter = location.pathname === '/' && !user;
    
    // Don't show UserTabBar on dashboard page (it has its own tab system)
    const hiddenUserTabBarPages = ['/dashboard', '/admin', '/login', '/register', '/messages', '/notifications'];
    const showUserTabBar = !hiddenUserTabBarPages.some(p => location.pathname.startsWith(p)) && !location.hash?.includes('session_id=');
    // TODO: Temporarily hide UserTabBar on desktop - show only on mobile
    const showUserTabBarMobileOnly = showUserTabBar;

    // Show maintenance banner only for non-admin users and not on admin pages
    const showMaintenanceBanner = !isAdmin && !isAdminPage && !isAuthPage;
    
    // Load custom favicon from settings
    useEffect(() => {
        const loadFavicon = async () => {
            try {
                const res = await settingsAPI.get();
                if (res.data.favicon_url) {
                    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
                    link.type = 'image/x-icon';
                    link.rel = 'shortcut icon';
                    link.href = res.data.favicon_url;
                    document.getElementsByTagName('head')[0].appendChild(link);
                }
            } catch (error) {
                // Ignore - use default favicon
            }
        };
        loadFavicon();
    }, []);

    // Track page visits for analytics
    useEffect(() => {
        const trackVisit = async () => {
            try {
                const API_URL = process.env.REACT_APP_BACKEND_URL;
                await fetch(`${API_URL}/api/admin/track-visit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        path: location.pathname,
                        referrer: document.referrer || ''
                    })
                });
            } catch (error) {
                // Silently fail - analytics shouldn't break the app
            }
        };
        trackVisit();
    }, [location.pathname]);

    return (
        <div className="App flex flex-col min-h-screen w-full max-w-[100vw]">
            {!isAuthPage && <Navbar />}
            <main className="flex-1 w-full max-w-[100vw] pt-12 lg:pt-0">
                <AppRouter />
            </main>
            {!isAuthPage && showFooter && <Footer />}
            <Toaster position="bottom-right" />
            <CookieBanner />
            {showMaintenanceBanner && <MaintenanceBanner />}
            {!isAuthPage && <FloatingMenu />}
            {!isAuthPage && <AdminFloatingMenu />}
        </div>
    );
};

function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <AuthProvider>
                    <UserCountsProvider>
                        <NotificationsProvider>
                            <AppLayout />
                        </NotificationsProvider>
                    </UserCountsProvider>
                </AuthProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
